import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { usePublicItem } from '../hooks/usePublicItem';
import { motion, AnimatePresence } from 'framer-motion';

export default function PillDetail() {
  const { id } = useParams();
  const { data: pill, loading } = usePublicItem('pills', id);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (pill?.slides && pill.slides.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % pill.slides.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [pill]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yüklənir...</p>
        </div>
      </div>
    );
  }

  if (!pill) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Məlumat tapılmadı</p>
      </div>
    );
  }

  const getAnimation = () => {
    switch (pill.animation_type) {
      case 'fade':
        return { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
      case 'slide':
        return { initial: { x: 100, opacity: 0 }, animate: { x: 0, opacity: 1 }, exit: { x: -100, opacity: 0 } };
      case 'scale':
        return { initial: { scale: 0.8, opacity: 0 }, animate: { scale: 1, opacity: 1 }, exit: { scale: 0.8, opacity: 0 } };
      default:
        return { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
    }
  };

  const animationProps = getAnimation();
  const contentIsHtml = typeof pill.content === 'string' && /<[a-z][\s\S]*>/i.test(pill.content);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8">
            <h1 className="text-4xl font-bold mb-2">{pill.label}</h1>
            <p className="text-blue-100">Ətraflı məlumat</p>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Image */}
            {pill.image && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-8"
              >
                <img
                  src={pill.image}
                  alt={pill.label}
                  className="w-full h-96 object-cover rounded-xl shadow-lg"
                />
              </motion.div>
            )}

            {/* Text Content */}
            {pill.content && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mb-8"
              >
                <div className="prose prose-lg max-w-none">
                  {contentIsHtml ? (
                    <div className="rich-content" dangerouslySetInnerHTML={{ __html: pill.content }} />
                  ) : (
                    <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {pill.content}
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Slides */}
            {pill.slides && pill.slides.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="mt-8"
              >
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Slaydlar</h2>
                <div className="relative bg-gray-900 rounded-xl overflow-hidden aspect-video">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={currentSlide}
                      src={pill.slides[currentSlide]}
                      alt={`Slide ${currentSlide + 1}`}
                      {...animationProps}
                      transition={{ duration: 0.5 }}
                      className="w-full h-full object-contain"
                    />
                  </AnimatePresence>
                  
                  {/* Slide Indicators */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                    {pill.slides.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-3 h-3 rounded-full transition-all ${
                          index === currentSlide ? 'bg-white scale-125' : 'bg-white/50'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Navigation Arrows */}
                  {pill.slides.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentSlide((prev) => (prev - 1 + pill.slides.length) % pill.slides.length)}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-2 rounded-full transition-all"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setCurrentSlide((prev) => (prev + 1) % pill.slides.length)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-2 rounded-full transition-all"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
